import { useAuthStore } from '@/stores/auth'
import type { SupervisedActionRequest, SupervisedActionResponse } from './types'

interface ResolvedAccountContext {
  ok: boolean
  accountId?: string
  error?: {
    code: string
    message: string
  }
}

function getApiUrl(): string {
  return import.meta.env.VITE_API_URL || window.location.origin
}

function buildHeaders(accountId: string, accessToken: string): HeadersInit {
  return {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
    'X-Account-Id': accountId
  }
}

function isLocalDevOrigin(origin: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin)
    || origin === 'skills-panel'
}

function parseAccountIdFromHostedOrigin(origin: string): string | null {
  try {
    const { hostname } = new URL(origin)
    const match = hostname.match(/^c-[^-]+-([a-f0-9]{32})-(\d+)\.(connect|devpreview)\.radsuite\.com$/i)
    if (!match) {
      return null
    }

    const compactAccountId = match[1].toLowerCase()
    return `${compactAccountId.slice(0, 8)}-${compactAccountId.slice(8, 12)}-${compactAccountId.slice(12, 16)}-${compactAccountId.slice(16, 20)}-${compactAccountId.slice(20)}`
  } catch {
    return null
  }
}

function resolveEffectiveAccountId(request: SupervisedActionRequest): ResolvedAccountContext {
  const authStore = useAuthStore()
  const parentAccountId = authStore.account?.id
  const requestAccountId = request.accountId
  const origin = request.metadata?.origin || ''

  if (!parentAccountId) {
    return {
      ok: false,
      error: {
        code: 'ACCOUNT_REQUIRED',
        message: 'No active parent account is selected.'
      }
    }
  }

  if (isLocalDevOrigin(origin)) {
    return {
      ok: true,
      accountId: parentAccountId
    }
  }

  const hostedOriginAccountId = parseAccountIdFromHostedOrigin(origin)
  if (hostedOriginAccountId) {
    if (hostedOriginAccountId !== parentAccountId) {
      return {
        ok: false,
        error: {
          code: 'ACCOUNT_SCOPE_MISMATCH',
          message: 'Hosted channel origin account does not match the active parent account.'
        }
      }
    }

    if (requestAccountId && requestAccountId !== hostedOriginAccountId) {
      return {
        ok: false,
        error: {
          code: 'REQUEST_ACCOUNT_MISMATCH',
          message: 'Request account does not match the hosted channel origin account.'
        }
      }
    }

    return {
      ok: true,
      accountId: hostedOriginAccountId
    }
  }

  return {
    ok: false,
    error: {
      code: 'UNTRUSTED_ORIGIN',
      message: `Supervisor requests are not allowed from origin: ${origin || 'unknown'}`
    }
  }
}

async function executeSkillsPublish(request: SupervisedActionRequest, forcedType?: 'skill' | 'plugin'): Promise<SupervisedActionResponse> {
  const authStore = useAuthStore()
  const accessToken = authStore.session?.access_token

  if (!accessToken) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'AUTH_REQUIRED',
        message: 'An authenticated session is required to publish skills.'
      }
    }
  }

  const accountContext = resolveEffectiveAccountId(request)
  if (!accountContext.ok || !accountContext.accountId) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: accountContext.error || {
        code: 'ACCOUNT_REQUIRED',
        message: 'Unable to resolve account context.'
      }
    }
  }

  const payload = (request.payload || {}) as {
    name?: string
    description?: string
    public?: boolean
    files?: Record<string, string>
    type?: 'skill' | 'plugin'
  }

  const skillName = payload.name?.trim()
  if (!skillName) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'INVALID_PAYLOAD',
        message: 'skills.publish requires payload.name.'
      }
    }
  }

  const type = forcedType || payload.type
  if (type !== 'skill' && type !== 'plugin') {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'INVALID_PAYLOAD',
        message: 'skills.publish requires payload.type to be skill or plugin.'
      }
    }
  }

  const response = await fetch(`${getApiUrl()}/api/skills/${encodeURIComponent(skillName)}`, {
    method: 'POST',
    headers: buildHeaders(accountContext.accountId, accessToken),
    body: JSON.stringify({
      description: payload.description,
      public: payload.public,
      type,
      files: payload.files || {}
    })
  })

  const data = await response.json().catch(() => null)

  if (!response.ok) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'SKILL_PUBLISH_FAILED',
        message: data?.message || data?.error || `Failed to publish skill: ${response.status}`
      }
    }
  }

  return {
    requestId: request.requestId,
    ok: true,
    status: 'executed',
    data
  }
}

async function executeSkillsList(request: SupervisedActionRequest, forcedType?: 'skill' | 'plugin'): Promise<SupervisedActionResponse> {
  const authStore = useAuthStore()
  const accessToken = authStore.session?.access_token

  if (!accessToken) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'AUTH_REQUIRED',
        message: 'An authenticated session is required to list skills.'
      }
    }
  }

  const accountContext = resolveEffectiveAccountId(request)
  if (!accountContext.ok || !accountContext.accountId) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: accountContext.error || {
        code: 'ACCOUNT_REQUIRED',
        message: 'Unable to resolve account context.'
      }
    }
  }

  const payload = (request.payload || {}) as { search?: string; scope?: 'account' | 'system' | 'all'; type?: 'skill' | 'plugin' }
  const params = new URLSearchParams()
  params.set('scope', payload.scope || 'all')
  const type = forcedType || payload.type
  if (type) {
    params.set('type', type)
  }
  if (payload.search) {
    params.set('search', payload.search)
  }
  const response = await fetch(`${getApiUrl()}/api/skills?${params.toString()}`, {
    method: 'GET',
    headers: buildHeaders(accountContext.accountId, accessToken)
  })

  const data = await response.json().catch(() => null)

  if (!response.ok) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'SKILLS_LIST_FAILED',
        message: data?.message || data?.error || `Failed to list skills: ${response.status}`
      }
    }
  }

  return {
    requestId: request.requestId,
    ok: true,
    status: 'executed',
    data
  }
}

async function executeSkillsGet(request: SupervisedActionRequest): Promise<SupervisedActionResponse> {
  const authStore = useAuthStore()
  const accessToken = authStore.session?.access_token

  if (!accessToken) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'AUTH_REQUIRED',
        message: 'An authenticated session is required to read skill details.'
      }
    }
  }

  const accountContext = resolveEffectiveAccountId(request)
  if (!accountContext.ok || !accountContext.accountId) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: accountContext.error || {
        code: 'ACCOUNT_REQUIRED',
        message: 'Unable to resolve account context.'
      }
    }
  }

  const payload = (request.payload || {}) as { skillNameOrId?: string }
  const skillNameOrId = payload.skillNameOrId?.trim()

  if (!skillNameOrId) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'INVALID_PAYLOAD',
        message: 'skills.get requires payload.skillNameOrId.'
      }
    }
  }

  const response = await fetch(`${getApiUrl()}/api/skills/${encodeURIComponent(skillNameOrId)}`, {
    method: 'GET',
    headers: buildHeaders(accountContext.accountId, accessToken)
  })

  const data = await response.json().catch(() => null)

  if (!response.ok) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'SKILL_GET_FAILED',
        message: data?.message || data?.error || `Failed to get skill: ${response.status}`
      }
    }
  }

  return {
    requestId: request.requestId,
    ok: true,
    status: 'executed',
    data
  }
}

export async function executeSupervisedAction(request: SupervisedActionRequest): Promise<SupervisedActionResponse> {
  switch (request.action) {
    case 'skills.publish':
      return executeSkillsPublish(request)
    case 'plugins.publish':
      return executeSkillsPublish(request, 'plugin')
    case 'skills.list':
      return executeSkillsList(request)
    case 'plugins.list':
      return executeSkillsList(request, 'plugin')
    case 'skills.get':
    case 'plugins.get':
      return executeSkillsGet(request)
    default:
      return {
        requestId: request.requestId,
        ok: false,
        status: 'failed',
        error: {
          code: 'ACTION_NOT_IMPLEMENTED',
          message: `No executor is implemented for ${request.action}.`
        }
      }
  }
}
